import { Box, FormHelperText, Grid, styled, Switch } from "@mui/material";
import { CentralDatePicker, CentralDropdown, CentralTextField, Ico_Warning, Title, useSnackbar } from "@sagecentral/core-ui";
import { KeyGrid } from "components/shared";
import { TraceVisit } from "components/shared/audit";
import { TaskProgressPill } from "components/tasks";
import { useWorkItemContext, useWorkItemFieldUpdater, useWorkItemPermissions } from "hooks/work-items";
import { useMemo, useState } from "react";
import { TaskFocus } from "models/work-items";
import { useTheme } from "@mui/material";
import WorkItemTasksGrid from "../tasks-grid/WorkItemTasksGrid";
import { IFieldError } from "models/work-items/IStepItem";

const StyledSwitch = styled(Switch)(({ }) => ({
    width: 64,
    height: 32,
    padding: 6,
    '& .MuiSwitch-switchBase': {
        margin: 4,
        padding: 1,
        transform: "translateX(2px)",
        '&.Mui-checked': {
            transform: "translateX(30px)",
            '& .MuiSwitch-thumb:before': {
                backgroundColor: '#001A69'
            },
            '& + .MuiSwitch-track': {
                backgroundColor: '#FFFFFF',
                opacity: 1,
                borderColor: '#001A69'
            },
        },
    },
    '& .MuiSwitch-thumb': {
        width: 22,
        height: 22,
        '&:before': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            backgroundColor: 'white',
            borderStyle: 'solid',
            borderWidth: '1px 1px 1px 1px',
            borderColor: '#001A69'
        },
    },
    '& .MuiSwitch-track': {
        borderRadius: 20 / 2,
        backgroundColor: 'white',
        borderStyle: 'solid',
        borderWidth: '1px 1px 1px 1px',
        opacity: 1,
        "&:after, &:before": {
            fontSize: "12px",
            position: "absolute",
            top: "7px",
            fontWeight: "bold",
            color: '#333333'
        },
        "&:after": {
            content: "'NO'",
            left: "35px",
        },
        "&:before": {
            content: "'YES'",
            right: "34px"
        }
    },
}));

const isError = (error?: IFieldError | null) => {
    return !!error && error.type === 'error';
};

const isWarning = (error?: IFieldError | null) => {
    return !!error && error.type === 'warn';
}

const ErrorHelperText = (props: { field: string, error?: IFieldError | null }) => {
    const { field, error } = props;
    const theme = useTheme();
    console.log('ErrorHelperText', error, field);
    return (
        <FormHelperText
            sx={{
                mt: 1,
                display: 'flex',
                alignItems: 'center'
            }}
            error={isError(error)}
        >
            {isWarning(error) && <><Ico_Warning htmlColor={theme.palette.warning.dark} sx={{ width: 20, height: 20 }} />&nbsp;</>}
            {error?.message}
        </FormHelperText>
    )
};

const WorkItemOverview = () => {
    const { template, workItem, options, getOptionsForStatus } = useWorkItemContext();
    const { displaySnackbar } = useSnackbar();
    const theme = useTheme();
    const [descHasError, setDescHasError] = useState(false);

    const permissions = useWorkItemPermissions();

    const handleFieldSuccess = (message: string) => {
        displaySnackbar('success', `Change has been successful. ${message}`);
    };

    const handleShowRunningLateChange = () => {


        var newShowRunningLateOnlyValue = !showRunningLateOnly;
        setShowRunningLateOnly(newShowRunningLateOnlyValue)
        setFocus(newShowRunningLateOnlyValue ? 'late-no-risk' : null);
    };

    const [setDescription, descriptionWorking] = useWorkItemFieldUpdater('description', {
        onUpdate: () => handleFieldSuccess('Description has been updated.'),
    });
    const [setStatus, statusWorking] = useWorkItemFieldUpdater('status', {
        onUpdate: () => handleFieldSuccess('Status has been updated.')
    });
    const [setStartDate, startDateWorking] = useWorkItemFieldUpdater('start-date', {
        onUpdate: () => handleFieldSuccess('Dates have been adjusted accordingly')
    });
    const [setContentType, contentTypeWorking] = useWorkItemFieldUpdater('content-type', {
        onUpdate: () => handleFieldSuccess('Content type has been updated.')
    });
    const [setFinancialDivision, financialDivisionWorking] = useWorkItemFieldUpdater('financial-division', {
        onUpdate: () => handleFieldSuccess('Financial division has been updated.')
    });
    const [setCompany, companyWorking] = useWorkItemFieldUpdater('company', {
        onUpdate: () => handleFieldSuccess('Company has been updated.')
    });

    const statusOptions = useMemo(() => {
        if (workItem === null) return [];
        if (permissions === null) return [];
        return getOptionsForStatus(permissions, workItem.statusId);
    }, [workItem, permissions, getOptionsForStatus]);
    const [focus, setFocus] = useState<TaskFocus>('late-no-risk');
    const [showRunningLateOnly, setShowRunningLateOnly] = useState(true);

    const handleChange = (newValue: string, workItemIdOverride?: number) => {
        if (newValue.length > 300) {
            setDescHasError(true);
            return;
        }
        setDescHasError(false);
        setDescription(newValue, workItemIdOverride);
    };

    return (
        <>
            <TraceVisit description='Work Item Overview Tab' data={{
                workItemId: workItem?.id ?? 0
            }} />
            <Grid container spacing={1}>
                <Grid container spacing={2}>
                    <Grid container item xs={12} sx={{ display: "flex" }}>
                        <Grid item sx={{ width: 200, minWidth: 150,  textAlign: "right" }}>
                            <KeyGrid>Progress:</KeyGrid>
                        </Grid>
                        <Grid item sx={{ flex: 1 }}>
                            {workItem?.statusId === 2 && <TaskProgressPill progress={workItem?.progress} />}
                        </Grid>
                    </Grid>

                    <Grid container item xs={12} sx={{ display: "flex" }}>
                        <Grid item sx={{ width: 200, minWidth: 150,  textAlign: "right" }}>
                            <KeyGrid>Status:</KeyGrid>
                        </Grid>
                        <Grid item sx={{ flex: 1 }}>
                            <CentralDropdown
                                value={workItem?.statusId}
                                options={statusOptions}
                                applyValue={setStatus}
                                working={statusWorking}
                                readonly={!permissions?.canChangeWorkItemStatus || (workItem?.statusId === 5 && !permissions?.canReopenWorkItem)}
                                tabIndex={1}
                                aria-label="Work Item Status"
                            />
                        </Grid>
                    </Grid>

                    <Grid container item xs={12} sx={{ display: "flex" }}>
                        <Grid item sx={{ width: 200, minWidth: 150,  textAlign: "right" }}>
                            <KeyGrid>Start Date:</KeyGrid>
                        </Grid>
                        <Grid item sx={{ flex: 1 }}>
                            <CentralDatePicker 
                                readonly 
                                value={workItem?.startDate} 
                                applyValue={setStartDate} 
                                working={startDateWorking} 
                                tabIndex={2}
                                aria-label="Work Item Start Date"
                            />
                        </Grid>
                    </Grid>

                    <Grid container item xs={12} sx={{ display: "flex" }}>
                        <Grid item sx={{ width: 200, minWidth: 150,  textAlign: "right" }}>
                            <KeyGrid>End Date:</KeyGrid>
                        </Grid>
                        <Grid item sx={{ flex: 1 }}>
                            <CentralDatePicker 
                                readonly 
                                value={workItem?.endDate} 
                                applyValue={() => { }} 
                                working={startDateWorking} 
                                tabIndex={3}
                                aria-label="Work Item End Date"
                            />
                        </Grid>
                    </Grid>

                    <Grid container item xs={12} sx={{ display: "flex" }}>
                        <Grid item sx={{ width: 200, minWidth: 150,  textAlign: "right" }}>
                            <KeyGrid>Work Item Template:</KeyGrid>
                        </Grid>
                        <Grid item sx={{ flex: 1 }}>{template?.name}</Grid>
                    </Grid>

                    <Grid container item xs={12} sx={{ display: "flex" }}>
                        <Grid item sx={{ width: 200, minWidth: 150,  textAlign: "right" }}>
                            <KeyGrid>Workspace:</KeyGrid>
                        </Grid>
                        <Grid item sx={{ flex: 1 }}>{workItem?.workspaceName}</Grid>
                    </Grid>

                    <Grid container item xs={12} sx={{ display: "flex" }}>
                        <Grid item sx={{ width: 200, minWidth: 150,  textAlign: "right" }}>
                            <KeyGrid>Content Type:</KeyGrid>
                        </Grid>
                        <Grid item sx={{ flex: 1 }}>
                            <CentralDropdown 
                                value={workItem?.contentTypeId} 
                                options={options?.contentTypes ?? []} 
                                applyValue={setContentType} 
                                working={contentTypeWorking}
                                tabIndex={4}
                                aria-label="Work Item Content Type" 
                            />
                        </Grid>
                    </Grid>

                    <Grid container item xs={12} sx={{ display: "flex" }}>
                        <Grid item sx={{ width: 200, minWidth: 150,  textAlign: "right" }}>
                            <KeyGrid>Financial Division:</KeyGrid>
                        </Grid>
                        <Grid item sx={{ flex: 1 }}>
                            <CentralDropdown 
                                value={workItem?.financialDivisionId} 
                                options={options?.financialDivisions ?? []} 
                                applyValue={setFinancialDivision} 
                                working={financialDivisionWorking}
                                tabIndex={5}
                                aria-label="Work Item Financial Division" 
                            />
                        </Grid>
                    </Grid>

                    <Grid container item xs={12} sx={{ display: "flex" }}>
                        <Grid item sx={{ width: 200, minWidth: 150,  textAlign: "right" }}>
                            <KeyGrid>Company:</KeyGrid>
                        </Grid>
                        <Grid item sx={{ flex: 1 }}>
                            <CentralDropdown 
                                value={workItem?.companyId} 
                                options={options?.companies ?? []} 
                                applyValue={setCompany} 
                                working={companyWorking}
                                tabIndex={6}
                                aria-label="Work Item Company" 
                            />
                        </Grid>
                    </Grid>

                    {workItem?.assetId && (
                        <Grid container item xs={12} sx={{ display: "flex" }}>
                            <Grid item sx={{ width: 200, minWidth: 150,  textAlign: "right" }}>
                                <KeyGrid>Sage Asset Identifier:</KeyGrid>
                            </Grid>
                            <Grid item sx={{ flex: 1 }}>{workItem?.assetId}</Grid>
                        </Grid>
                    )}

                    {workItem?.assetName && (
                        <Grid container item xs={12} sx={{ display: "flex" }}>
                            <Grid item sx={{ width: 200, minWidth: 150,  textAlign: "right" }}>
                                <KeyGrid>Asset Name:</KeyGrid>
                            </Grid>
                            <Grid item sx={{ flex: 1 }}>{workItem?.assetName}</Grid>
                        </Grid>
                    )}

                    <Grid container item xs={12} sx={{ display: "flex" }}>
                        <Grid item sx={{ width: 200, minWidth: 150,  textAlign: "right" }}>
                            <KeyGrid>Description:</KeyGrid>
                        </Grid>
                        <Grid item sx={{ flex: 1 }}>
                            <CentralTextField 
                                value={workItem?.description} 
                                applyValue={handleChange} 
                                working={descriptionWorking} 
                                fullWidth
                                tabIndex={7}
                                aria-label="Work Item Description"
                            />
                            <br />
                            <ErrorHelperText field="description" error={descHasError ? { type: 'error', message: 'Allows maximum 300 characters.' } : null} />
                        </Grid>
                    </Grid>
                </Grid>

                {permissions?.canViewWorkflows && <>
                    <Grid item xs={12}>
                        <Title
                            theme={theme}
                            sx={{
                                marginBottom: '24px',
                                marginTop: '32px'
                            }}
                            size='xs'>Tasks In Progress</Title>

                        <Box>
                            <label id="late-tasks-switch-label" htmlFor="late-tasks-switch">Show running late tasks only: </label>
                            <StyledSwitch 
                                onChange={handleShowRunningLateChange} 
                                checked={showRunningLateOnly}
                                inputProps={{
                                    id: "late-tasks-switch",
                                    role: "switch",
                                    "aria-checked": showRunningLateOnly,
                                    "aria-labelledby": "late-tasks-switch-label",
                                    tabIndex: 8
                                }}
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <WorkItemTasksGrid
                            statusFilter='in-progress'
                            all
                            showUser
                            focus={focus}
                            workitem={workItem}
                            aria-labelledby="tasks-in-progress-grid-label"
                            tabIndex={9}
                         />
                    </Grid>
                </>}

            </Grid>
        </>
    )
};

export default WorkItemOverview;